import { useState, type ReactNode } from 'react';
import { DsButton, DsInput, DsTextarea } from '@/modules/internal-v2/ds';
import type { Item } from '@/api/items/items.types.ts';
import type {
  BannerBlockData,
  ButtonBlockData,
  EmailBlock,
  EmailBlockData,
  HeadingBlockData,
  ImageBlockData,
  InfoBoxBlockData,
  ProductCardBlockData,
  ProductListBlockData,
  ProductListItem,
  ProductSpotlightBlockData,
  SpacerBlockData,
  TextBlockData,
} from '@/api/email-builder/email-builder.types.ts';
import { itemLinkFor } from '../email-builder.constants';
import { cardFromItem } from '../product-blocks';
import { RichTextEditor } from './rich-text-editor.component';
import { ItemPickerModal } from './item-picker-modal.component';

/* ------------------------------------------------------------------ */
/* Shared field primitives                                            */
/* ------------------------------------------------------------------ */

function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
}) {
  return (
    <div className="eb-field">
      <span className="eb-field-label">{label}</span>
      {children}
      {hint && <span className="eb-field-hint">{hint}</span>}
    </div>
  );
}

function TextInput({
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <DsInput
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: SegmentedOption<T>[];
  onChange: (value: T) => void;
}) {
  return (
    <div className="eb-segmented" role="group">
      {options.map((option) => (
        <button
          type="button"
          key={option.value}
          className={value === option.value ? 'active' : ''}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Content block editors                                              */
/* ------------------------------------------------------------------ */

function HeadingEditor({
  data,
  onChange,
}: {
  data: HeadingBlockData;
  onChange: (data: HeadingBlockData) => void;
}) {
  return (
    <>
      <Field label="Text">
        <TextInput
          value={data.text}
          onChange={(text) => onChange({ ...data, text })}
          placeholder="Überschrift"
        />
      </Field>
      <Field label="Größe">
        <Segmented
          value={data.level}
          onChange={(level) => onChange({ ...data, level })}
          options={[
            { value: 'h1', label: 'H1' },
            { value: 'h2', label: 'H2' },
            { value: 'h3', label: 'H3' },
          ]}
        />
      </Field>
    </>
  );
}

function TextEditor({
  data,
  onChange,
}: {
  data: TextBlockData;
  onChange: (data: TextBlockData) => void;
}) {
  return (
    <Field label="Inhalt">
      <RichTextEditor
        html={data.html}
        onChange={(html) => onChange({ ...data, html })}
      />
    </Field>
  );
}

function ButtonEditor({
  data,
  onChange,
}: {
  data: ButtonBlockData;
  onChange: (data: ButtonBlockData) => void;
}) {
  return (
    <>
      <Field label="Beschriftung">
        <TextInput
          value={data.label}
          onChange={(label) => onChange({ ...data, label })}
          placeholder="Mehr erfahren"
        />
      </Field>
      <Field label="Link (URL)">
        <TextInput
          value={data.href}
          onChange={(href) => onChange({ ...data, href })}
          placeholder="https://…"
        />
      </Field>
      <Field label="Stil">
        <Segmented
          value={data.variant}
          onChange={(variant) => onChange({ ...data, variant })}
          options={[
            { value: 'primary', label: 'Primär' },
            { value: 'secondary', label: 'Sekundär' },
          ]}
        />
      </Field>
    </>
  );
}

function ImageEditor({
  data,
  onChange,
}: {
  data: ImageBlockData;
  onChange: (data: ImageBlockData) => void;
}) {
  return (
    <>
      <Field label="Bild-URL">
        <TextInput
          value={data.src}
          onChange={(src) => onChange({ ...data, src })}
          placeholder="https://…/bild.jpg"
        />
      </Field>
      <Field label="Alternativtext">
        <TextInput
          value={data.alt}
          onChange={(alt) => onChange({ ...data, alt })}
          placeholder="Bildbeschreibung"
        />
      </Field>
      <Field label="Link (optional)">
        <TextInput
          value={data.href}
          onChange={(href) => onChange({ ...data, href })}
          placeholder="https://…"
        />
      </Field>
      <Field label="Bildunterschrift (optional)">
        <TextInput
          value={data.caption}
          onChange={(caption) => onChange({ ...data, caption })}
        />
      </Field>
    </>
  );
}

function InfoBoxEditor({
  data,
  onChange,
}: {
  data: InfoBoxBlockData;
  onChange: (data: InfoBoxBlockData) => void;
}) {
  return (
    <>
      <Field label="Variante">
        <Segmented
          value={data.variant}
          onChange={(variant) => onChange({ ...data, variant })}
          options={[
            { value: 'info', label: 'Info' },
            { value: 'warning', label: 'Warnung' },
            { value: 'success', label: 'Erfolg' },
          ]}
        />
      </Field>
      <Field label="Titel (optional)">
        <TextInput
          value={data.title}
          onChange={(title) => onChange({ ...data, title })}
        />
      </Field>
      <Field label="Inhalt">
        <RichTextEditor
          simple
          html={data.html}
          onChange={(html) => onChange({ ...data, html })}
        />
      </Field>
    </>
  );
}

function BannerEditor({
  data,
  onChange,
}: {
  data: BannerBlockData;
  onChange: (data: BannerBlockData) => void;
}) {
  return (
    <>
      <Field label="Text">
        <TextInput
          value={data.text}
          onChange={(text) => onChange({ ...data, text })}
        />
      </Field>
      <Field label="Button-Beschriftung (optional)">
        <TextInput
          value={data.buttonLabel}
          onChange={(buttonLabel) => onChange({ ...data, buttonLabel })}
        />
      </Field>
      <Field label="Button-Link (optional)">
        <TextInput
          value={data.buttonHref}
          onChange={(buttonHref) => onChange({ ...data, buttonHref })}
          placeholder="https://…"
        />
      </Field>
    </>
  );
}

function SpacerEditor({
  data,
  onChange,
}: {
  data: SpacerBlockData;
  onChange: (data: SpacerBlockData) => void;
}) {
  return (
    <Field label="Höhe" hint={`${data.height} px`}>
      <input
        type="range"
        className="eb-range"
        min={8}
        max={96}
        step={4}
        value={data.height}
        onChange={(e) => onChange({ ...data, height: Number(e.target.value) })}
      />
    </Field>
  );
}

function DividerEditor() {
  return (
    <p className="eb-empty-hint">
      Trennlinie — keine weiteren Einstellungen.
    </p>
  );
}

/* ------------------------------------------------------------------ */
/* Product block editors (SAP item picker)                            */
/* ------------------------------------------------------------------ */

function PickFromCatalogButton({
  onPick,
  multiple = false,
}: {
  onPick: (item: Item) => void;
  multiple?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <DsButton
        type="button"
        variant="primary"
        size="small"
        onClick={() => setOpen(true)}
      >
        Artikel aus Katalog wählen
      </DsButton>
      <ItemPickerModal
        open={open}
        multiple={multiple}
        onClose={() => setOpen(false)}
        onPick={onPick}
      />
    </>
  );
}

function ProductCardEditor({
  data,
  onChange,
}: {
  data: ProductCardBlockData;
  onChange: (data: ProductCardBlockData) => void;
}) {
  return (
    <>
      <div className="eb-field">
        <PickFromCatalogButton
          onPick={(item) => onChange({ ...cardFromItem(item) })}
        />
      </div>
      <Field label="Produktname">
        <TextInput
          value={data.name}
          onChange={(name) => onChange({ ...data, name })}
        />
      </Field>
      <Field label="Artikelnummer">
        <TextInput
          value={data.articleNumber}
          onChange={(articleNumber) => onChange({ ...data, articleNumber })}
        />
      </Field>
      <Field label="Hersteller">
        <TextInput
          value={data.manufacturer}
          onChange={(manufacturer) => onChange({ ...data, manufacturer })}
        />
      </Field>
      <Field label="Bild-URL">
        <TextInput
          value={data.imageUrl}
          onChange={(imageUrl) => onChange({ ...data, imageUrl })}
        />
      </Field>
      <Field
        label="Preis"
        hint="Leer lassen für den individuellen Preis des Empfängers."
      >
        <TextInput
          value={data.price}
          onChange={(price) => onChange({ ...data, price })}
          placeholder="Individueller Preis des Empfängers"
        />
      </Field>
      <Field label="Link">
        <TextInput
          value={data.href}
          onChange={(href) => onChange({ ...data, href })}
        />
      </Field>
    </>
  );
}

function ProductSpotlightEditor({
  data,
  onChange,
}: {
  data: ProductSpotlightBlockData;
  onChange: (data: ProductSpotlightBlockData) => void;
}) {
  return (
    <>
      <div className="eb-field">
        <PickFromCatalogButton
          onPick={(item) =>
            onChange({
              ...data,
              name: item.name,
              articleNumber: item.id,
              imageUrl: item.imageUrl ?? '',
              price: '',
              manufacturer: item.manufacturer,
              buttonHref: itemLinkFor(item.id),
            })
          }
        />
      </div>
      <Field label="Produktname">
        <TextInput
          value={data.name}
          onChange={(name) => onChange({ ...data, name })}
        />
      </Field>
      <Field label="Artikelnummer">
        <TextInput
          value={data.articleNumber}
          onChange={(articleNumber) => onChange({ ...data, articleNumber })}
        />
      </Field>
      <Field label="Hersteller">
        <TextInput
          value={data.manufacturer}
          onChange={(manufacturer) => onChange({ ...data, manufacturer })}
        />
      </Field>
      <Field label="Beschreibung">
        <DsTextarea
          className="eb-textarea"
          value={data.description}
          onChange={(e) => onChange({ ...data, description: e.target.value })}
        />
      </Field>
      <Field label="Bild-URL">
        <TextInput
          value={data.imageUrl}
          onChange={(imageUrl) => onChange({ ...data, imageUrl })}
        />
      </Field>
      <Field
        label="Preis"
        hint="Leer lassen für den individuellen Preis des Empfängers."
      >
        <TextInput
          value={data.price}
          onChange={(price) => onChange({ ...data, price })}
          placeholder="Individueller Preis des Empfängers"
        />
      </Field>
      <Field label="Button-Beschriftung">
        <TextInput
          value={data.buttonLabel}
          onChange={(buttonLabel) => onChange({ ...data, buttonLabel })}
        />
      </Field>
      <Field label="Button-Link">
        <TextInput
          value={data.buttonHref}
          onChange={(buttonHref) => onChange({ ...data, buttonHref })}
        />
      </Field>
    </>
  );
}

function ProductListEditor({
  data,
  onChange,
}: {
  data: ProductListBlockData;
  onChange: (data: ProductListBlockData) => void;
}) {
  const setItem = (index: number, patch: Partial<ProductListItem>) => {
    onChange({
      ...data,
      items: data.items.map((item, i) =>
        i === index ? { ...item, ...patch } : item,
      ),
    });
  };

  const removeItem = (index: number) => {
    onChange({ ...data, items: data.items.filter((_, i) => i !== index) });
  };

  return (
    <>
      <Field label="Titel (optional)">
        <TextInput
          value={data.title}
          onChange={(title) => onChange({ ...data, title })}
        />
      </Field>

      <div className="eb-field">
        <span className="eb-field-label">Produkte ({data.items.length})</span>
        {data.items.length === 0 ? (
          <span className="eb-field-hint">
            Noch keine Produkte — über den Katalog hinzufügen.
          </span>
        ) : (
          <span className="eb-field-hint">
            Preis leer lassen für den individuellen Preis des Empfängers.
          </span>
        )}
        {data.items.map((item, index) => (
          <div className="eb-list-item" key={index}>
            <div className="eb-list-item-fields">
              <DsInput
                value={item.name}
                placeholder="Produktname"
                onChange={(e) => setItem(index, { name: e.target.value })}
              />
              <div className="eb-list-item-row">
                <DsInput
                  value={item.articleNumber}
                  placeholder="Art.-Nr."
                  onChange={(e) =>
                    setItem(index, { articleNumber: e.target.value })
                  }
                />
                <DsInput
                  value={item.price}
                  placeholder="Individueller Preis"
                  onChange={(e) => setItem(index, { price: e.target.value })}
                />
              </div>
            </div>
            <button
              type="button"
              className="eb-icon-btn"
              title="Produkt entfernen"
              onClick={() => removeItem(index)}
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      <div className="eb-field">
        <PickFromCatalogButton
          multiple
          onPick={(item) =>
            onChange({
              ...data,
              items: [
                ...data.items,
                {
                  name: item.name,
                  articleNumber: item.id,
                  price: '',
                },
              ],
            })
          }
        />
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Dispatcher                                                         */
/* ------------------------------------------------------------------ */

interface BlockEditorProps {
  block: EmailBlock;
  onChange: (data: EmailBlockData) => void;
}

/** Renders the field editor matching `block.type`. */
export function BlockEditor({ block, onChange }: BlockEditorProps) {
  switch (block.type) {
    case 'heading':
      return (
        <HeadingEditor data={block.data as HeadingBlockData} onChange={onChange} />
      );
    case 'text':
      return <TextEditor data={block.data as TextBlockData} onChange={onChange} />;
    case 'button':
      return (
        <ButtonEditor data={block.data as ButtonBlockData} onChange={onChange} />
      );
    case 'image':
      return (
        <ImageEditor data={block.data as ImageBlockData} onChange={onChange} />
      );
    case 'info-box':
      return (
        <InfoBoxEditor
          data={block.data as InfoBoxBlockData}
          onChange={onChange}
        />
      );
    case 'banner':
      return (
        <BannerEditor data={block.data as BannerBlockData} onChange={onChange} />
      );
    case 'divider':
      return <DividerEditor />;
    case 'spacer':
      return (
        <SpacerEditor data={block.data as SpacerBlockData} onChange={onChange} />
      );
    case 'product-card':
      return (
        <ProductCardEditor
          data={block.data as ProductCardBlockData}
          onChange={onChange}
        />
      );
    case 'product-spotlight':
      return (
        <ProductSpotlightEditor
          data={block.data as ProductSpotlightBlockData}
          onChange={onChange}
        />
      );
    case 'product-list':
      return (
        <ProductListEditor
          data={block.data as ProductListBlockData}
          onChange={onChange}
        />
      );
    default:
      return null;
  }
}
